import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { FullPageSpinner } from '@/components/FullPageSpinner';
import {
  MFA_REPLACE_AUTHENTICATOR_PATH,
  signInPathFor,
  useAuth,
  useSessionState,
  useSignOut,
} from '@/auth';
import { useApi } from '@/api/apiContext';
import { meQueryOptions } from '@/api/endpoints/me';
import { SecondFactorEnrollmentRequiredError } from '@/api/errors';
import { messages } from '@/i18n';
import { AuthScreenLayout } from './AuthScreenLayout';
import { PhoneEnrollmentStep } from './mfa/PhoneEnrollmentStep';
import { TotpEnrollmentStep } from './mfa/TotpEnrollmentStep';

/**
 * /security/enroll — where SessionGuard sends a user whose token has no second
 * factor. An authenticator app first, then a recovery phone; neither can be
 * skipped (backend decision D2: SMS recovery).
 *
 * Inside AuthGuard, outside SessionGuard: /me refuses this user until they
 * have enrolled, so nesting it inside SessionGuard would be a redirect loop.
 *
 * The step shown is derived from the factors already on the account, so a
 * reload mid-way resumes where it stopped.
 */
export function EnrollSecondFactors() {
  const t = messages.mfa;
  const { secondFactors, refreshToken } = useAuth();
  const { status } = useSessionState();
  const signOut = useSignOut();
  const api = useApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [finishing, setFinishing] = useState(false);

  const hasTotp = secondFactors.some((f) => f.kind === 'totp');
  const hasSms = secondFactors.some((f) => f.kind === 'sms');

  // Neither state is resolved by enrolling here.
  if (status === 'not-provisioned') return <Navigate to="/access-not-provisioned" replace />;
  if (status === 'mfa-reenrollment-required') {
    return <Navigate to={MFA_REPLACE_AUTHENTICATOR_PATH} replace />;
  }

  // Enrolled: get a token that says so, and ask /me again. Whether a refresh
  // is enough or the second factor only appears in a token from a fresh
  // sign-in is unconfirmed (backend assumption U4), so both are handled: if
  // /me still says "enroll", sign out and back in.
  const finish = async () => {
    setFinishing(true);
    try {
      await refreshToken();
      await queryClient.fetchQuery({ ...meQueryOptions(api), staleTime: 0 });
    } catch (error) {
      if (error instanceof SecondFactorEnrollmentRequiredError) {
        await signOut({ redirectTo: signInPathFor('second-factor-enrolled') });
        return;
      }
      // Anything else is SessionGuard's to route, from the root.
    }
    void navigate('/', { replace: true });
  };

  if (finishing) return <FullPageSpinner label={t.enrollFinishing} />;

  const step = !hasTotp ? 1 : 2;

  return (
    <AuthScreenLayout title={t.enrollTitle} description={t.enrollBody}>
      <div className="flex flex-col gap-4">
        {hasTotp && hasSms ? (
          // Both enrolled already — back after a reload, or a token that
          // predates the enrollment.
          <Button className="w-full" onClick={() => void finish()}>
            {t.enrollContinue}
          </Button>
        ) : (
          <>
            <p className="text-muted-foreground text-xs font-medium">
              {t.enrollStepProgress.replace('{step}', String(step)).replace('{total}', '2')}
            </p>
            {!hasTotp ? (
              // Enrolling updates the factor list, which moves this screen on.
              <TotpEnrollmentStep onEnrolled={() => undefined} />
            ) : (
              <PhoneEnrollmentStep onEnrolled={() => void finish()} />
            )}
          </>
        )}

        <button
          type="button"
          className="text-muted-foreground text-center text-xs hover:underline"
          onClick={() => void signOut()}
        >
          {t.enrollSignOut}
        </button>
      </div>
    </AuthScreenLayout>
  );
}
